/**
 * useDocumentTitle
 *
 * Single source of truth for the browser tab title across the app.
 * Feb 2026 — unified to "VÉRAULT Travels" as the canonical public-facing
 * brand title. On-page labels may still say "Member Suite" where
 * appropriate, but the BROWSER TAB always reads VÉRAULT Travels.
 *
 *   Public marketing pages (/, /about, /services, /plan, /blog, /guides,
 *   /destinations)                                    → "VÉRAULT Travels"
 *   Login + onboarding pages                          → "Suite Access · VÉRAULT Travels"
 *   Member Suite (/portal/client/*)                   → "VÉRAULT Travels"
 *   Advisor portal (/portal/advisor/* and /advisor/*) → "Advisor · VÉRAULT Travels"
 *   Jackson door (/suite/jackson)                     → "The Jackson Family · VÉRAULT Travels"
 *
 * Mounted once, in App.js — reads pathname on every route change.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

function resolveTitle(pathname) {
  const p = (pathname || "/").toLowerCase();
  if (p === "/suite/jackson") return "The Jackson Family · VÉRAULT Travels";
  if (p.startsWith("/suite/jackson")) return "Jackson · VÉRAULT Travels";
  if (p.startsWith("/suite/")) return "VÉRAULT Travels";
  if (p.startsWith("/portal/client")) return "VÉRAULT Travels";
  if (p.startsWith("/portal/advisor")) return "Advisor · VÉRAULT Travels";
  if (p.startsWith("/advisor")) return "Advisor · VÉRAULT Travels";
  if (p === "/login" || p === "/forgot-password" || p === "/reset-password" || p === "/setup-account") {
    return "Suite Access · VÉRAULT Travels";
  }
  // Public marketing
  return "VÉRAULT Travels";
}

// Feb 2026 SEO foundation — paths that must NEVER be indexed by search engines.
// Includes every private/member surface, every auth door, and any advisor
// or owner workspace. Public marketing routes (/, /about, /services, /plan,
// /destinations, /contact, /blog, /guides) are explicitly allowed.
const PRIVATE_PREFIXES = [
  "/portal", "/advisor", "/owner", "/owner-access", "/owner-hub",
  "/internal-links", "/admin", "/suite", "/trip", "/itinerary",
  "/client", "/clients", "/access", "/login", "/forgot-password",
  "/reset-password", "/setup-account", "/preview",
];

function isPrivatePath(pathname) {
  const p = (pathname || "/").toLowerCase();
  return PRIVATE_PREFIXES.some((pref) => p === pref || p.startsWith(pref + "/") || p.startsWith(pref + "?"));
}

const CANONICAL_HOST = "https://travel.verault.co";

function setMeta(name, content) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  if (typeof document === "undefined") return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function DocumentTitleEffect() {
  const { pathname } = useLocation();
  useEffect(() => {
    const next = resolveTitle(pathname);
    if (typeof document !== "undefined" && document.title !== next) {
      document.title = next;
    }
    // Feb 2026 SEO foundation — set robots + canonical per route.
    // Private/member surfaces get noindex,nofollow,noarchive,nosnippet
    // so they never leak into search engines or LLM training crawls.
    // Public marketing routes get index,follow with a path-correct
    // canonical pointing at the official domain.
    if (isPrivatePath(pathname)) {
      setMeta("robots", "noindex,nofollow,noarchive,nosnippet");
      setMeta("googlebot", "noindex,nofollow");
      // No canonical for private routes — they aren't meant to be discovered.
      setCanonical(`${CANONICAL_HOST}/`);
    } else {
      setMeta("robots", "index,follow,max-image-preview:large");
      setMeta("googlebot", "index,follow");
      setCanonical(`${CANONICAL_HOST}${pathname || "/"}`);
    }
  }, [pathname]);
  return null;
}
